import { observer } from 'mobx-lite';
import { Chip, Stack, Typography, Button, Box, Paper } from '@mui/material';
import {
    Videocam,
    VideocamOff,
    CellWifi,
    SignalWifiOff,
    Memory,
    Visibility,
    VisibilityOff,
    Face,
    Speed,
    PlayArrow,
    Stop,
    Info,
} from '@mui/icons-material';
import { MainStoreContextItem } from '../Stores/MainStore';
import { FaceSdkStoreContextItem } from '../Stores/FaceSdkStore';

export const StatusIndicator = observer(() => {
    const store = MainStoreContextItem.useStore();
    const faceSdkStore = FaceSdkStoreContextItem.useStore();

    const { permissionGranted, stream, loading, error } = store.mediaDeviceStore;
    const { faceSdk, sdkInitializing, error: sdkError } = faceSdkStore;
    const { isDetecting, faces, avgProcessImageTimeMs } = store.faceServiceStore;
    const { showNerdStatistics } = store.settingsStore;

    if (!showNerdStatistics) {
        return null;
    }

    const handleToggleDetection = () => {
        if (store.mediaDeviceStore.videoElementRef.current) {
            store.faceServiceStore.setVideoElement(store.mediaDeviceStore.videoElementRef.current);
        }
        store.faceServiceStore.toggleFaceDetection();
    };

    return (
        <Paper
            elevation={4}
            sx={{
                position: 'absolute',
                top: 10,
                right: 50,
                borderRadius: 1,
                minWidth: 250,
                zIndex: 1000,
                p: 2,
            }}
        >
            <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={700}>
                <Info sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                System Status
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                    icon={permissionGranted ? <Videocam /> : <VideocamOff />}
                    label={`Camera: ${permissionGranted ? 'Granted' : 'Denied'}`}
                    color={permissionGranted ? 'success' : 'error'}
                    size="small"
                />
                <Chip
                    icon={stream ? <CellWifi /> : <SignalWifiOff />}
                    label={`Stream: ${stream ? 'Active' : 'Inactive'}`}
                    color={stream ? 'success' : 'default'}
                    size="small"
                />
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                <Chip
                    icon={<Memory />}
                    label={`SDK: ${sdkInitializing ? 'Loading...' : faceSdk ? 'Ready' : 'Failed'}`}
                    color={faceSdk ? 'success' : sdkInitializing ? 'warning' : 'error'}
                    size="small"
                />
                <Chip
                    icon={isDetecting ? <Visibility /> : <VisibilityOff />}
                    label={`Detection: ${isDetecting ? 'Running' : 'Stopped'}`}
                    color={isDetecting ? 'success' : 'default'}
                    size="small"
                />
            </Box>

            <Chip
                icon={<Face />}
                label={`Faces Detected: ${faces?.length ?? 0}`}
                color={faces && faces.length > 0 ? 'primary' : 'default'}
                size="small"
            />

            {isDetecting && (
                <Typography variant="caption">
                    <Speed sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                    FPS - {avgProcessImageTimeMs > 0 ? (1000 / avgProcessImageTimeMs).toFixed(1) : 'N/A'}
                </Typography>
            )}

            <Button
                size="small"
                variant="outlined"
                color={isDetecting ? 'error' : 'success'}
                onClick={handleToggleDetection}
                disabled={!faceSdk || sdkInitializing || !stream}
                startIcon={isDetecting ? <Stop /> : <PlayArrow />}
            >
                {isDetecting ? 'Stop' : 'Start'} Detection
            </Button>

            {(error || sdkError) && (
                <Typography variant="caption" color="error">
                    Error: {error || sdkError}
                </Typography>
            )}

            {loading && (
                <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    Loading...
                </Typography>
            )}
            </Stack>
        </Paper>
    );
});

StatusIndicator.displayName = 'StatusIndicator';
